import {Box, Button, CircularProgress, DialogActions, DialogContent, Stack, Tab, Tabs, Typography} from '@mui/material'
import {IndicatorInput, IndicatorInput_InputType} from '../../../../proto/generated/indicator'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {SeriesOutput} from '../../chart.typings'
import SeriesGroupInputs from './seriesGroupInputs'
import { IndicatorOutputSourceOption } from '../../../../util/indicators'
import SeriesGroupOutputs from './seriesGroupOutputs'
import SeriesGroupOutputValuesSettings from './seriesGroupOutputValuesSettings'
import {MULTIPANE_FULLSCREEN_MODE_ID} from '../../../../assets/constants'
import {Security, SecurityCategory_CategoryCode} from '../../../../proto/generated/security'
import DraggableDialogTitle from '../../../common/DraggableDialogTitle'
import CustomDialog from '../../../common/CustomDialog'
import {ExtraSmallButton} from "../../../custom";
import ClearIcon from "../../../../assets/icons/ClearIcon";
import { ChartLineStyle } from "../../../colorSelector/lineStyle.types";
export type SeriesGroupConfigurationPopupProps = {
    id: string,
    viewKey: string,
    inputValues: Record<string, any>
    outputValues: Record<string, SeriesOutput>
    seriesOrder?: string[]
    activePlotTitles?: ReadonlySet<string>
    plotTitlesPending?: boolean
    fields: IndicatorInput[];
    title: string,
    outputValuesPrecision?: number
    showLabelOnPriceScale?: boolean
    showValueOnPriceScale?: boolean
    onInputsUpdate: (inputValues: Record<string, any>) => void,
    onOutputsUpdate: (outputValues: Record<string, SeriesOutput>) => void,
    onOutputValuesPrecisionUpdate: (precision: number | undefined) => void,
    onDisplaySettingsUpdate: (settings: { showLabelOnPriceScale?: boolean; showValueOnPriceScale?: boolean }) => void,
    onClose: () => void
    onSubmit: () => void
    security?: Security | undefined
    indicatorOutputSources?: IndicatorOutputSourceOption[]
    getSourceLabel?: (value: string) => string
    showLineStyle?: boolean
    lineStyle?: ChartLineStyle
    onLineStyleChange?: (style: ChartLineStyle) => void
    disableAreaFill?: boolean
    hideOutputValuesSettings?: boolean
    getDefaultInputValues?: () => Record<string, any>
    getDefaultOutputValues?: () => Record<string, SeriesOutput>
}

const SeriesGroupConfigurationPopup: React.FC<SeriesGroupConfigurationPopupProps> = ({
                                                                                         title,
                                                                                         fields,
                                                                                         inputValues,
                                                                                         viewKey,
                                                                                         onClose,
                                                                                         onSubmit,
                                                                                         outputValues: outputConfig,
                                                                                         seriesOrder,
                                                                                         activePlotTitles,
                                                                                         plotTitlesPending,
                                                                                         outputValuesPrecision,
                                                                                         showLabelOnPriceScale,
                                                                                         showValueOnPriceScale,
                                                                                         onInputsUpdate,
                                                                                         onOutputsUpdate,
                                                                                         onOutputValuesPrecisionUpdate,
                                                                                         onDisplaySettingsUpdate,
                                                                                         security,
                                                                                         indicatorOutputSources,
                                                                                         getSourceLabel,
                                                                                         showLineStyle,
                                                                                         lineStyle,
                                                                                         onLineStyleChange,
                                                                                         disableAreaFill,
                                                                                         hideOutputValuesSettings = false,
                                                                                         getDefaultInputValues,
                                                                                         getDefaultOutputValues,
                                                                                     }) => {
    const initialInputValues = useRef<Record<string, any>>(inputValues)
    const [draftInputValues, setDraftInputValues] = useState<Record<string, any>>(inputValues)
    const initialOutputValues = useRef<Record<string, SeriesOutput>>(outputConfig)
    const initialOutputValuesPrecision = useRef<number | undefined>(outputValuesPrecision)
    const initialShowLabelOnPriceScale = useRef<boolean | undefined>(showLabelOnPriceScale)
    const initialShowValueOnPriceScale = useRef<boolean | undefined>(showValueOnPriceScale)
    const disableInput = useMemo(() => {
        return title === 'Open Interest Outlook' && ![SecurityCategory_CategoryCode.STOCKS, SecurityCategory_CategoryCode.INDEX].includes(security?.categoryCode as any)
    }, [security?.categoryCode, title])

    const hasStyleTab = Object.keys(outputConfig).length > 0

    const hasApplicableStyleOutputs = useMemo(() => {
        if (plotTitlesPending) {
            return hasStyleTab
        }
        if (activePlotTitles) {
            return Object.values(outputConfig).some(
                (output) => output.title != null && activePlotTitles.has(output.title)
            )
        }
        return hasStyleTab
    }, [outputConfig, activePlotTitles, plotTitlesPending, hasStyleTab])

    const [selectedTab, setSelectedTab] = useState<string>(
        fields.length > 0 && !disableInput ? 'inputs' : hasStyleTab ? 'styles' : 'inputs'
    )

    const finalInputs = useCallback((values: Record<string, any>) => {
        const fValues: Record<string, any> = {}
        for (const key in values) {
            const val = values[key]
            if (val === '' || (typeof val === 'number' && isNaN(val))) {
                fValues[key] = '0'
            } else {
                fValues[key] = val
            }
        }
        return fValues
    }, [])

    useEffect(() => {
        setDraftInputValues(inputValues)
    }, [inputValues])

    const onInputDraftChange = (title: string, value: any) => {
        setSelectedTab('inputs')
        setDraftInputValues({
            ...draftInputValues,
            [title]: value
        })
    }

    const onInputChange = (title: string, value: any) => {
        setSelectedTab('inputs')
        const nextValues = finalInputs({
            ...draftInputValues,
            [title]: value
        })
        setDraftInputValues(nextValues)
        onInputsUpdate(nextValues)
    }

    const onOutputChange = (key: string, seriesOutput: SeriesOutput) => {
        const newOutputConfig = {...outputConfig}

        newOutputConfig[key] = seriesOutput
        onOutputsUpdate({
            ...newOutputConfig
        })

    }

    const tabs = useMemo(() => {
        return <Tabs
            value={selectedTab}
            onChange={(_event: React.SyntheticEvent, newTab: string) => {
                setSelectedTab(newTab)
            }}
            sx={{
                width: '100%',
                overflowX: 'auto',
                display: 'flex'
            }}
        >
            {
                fields.length > 0 && !disableInput && <Tab label={'Inputs'} value={'inputs'} sx={{textTransform: 'none'}}/>
            }
            {
                hasStyleTab && <Tab label={'Style'} value={'styles'} sx={{textTransform: 'none'}}/>
            }
        </Tabs>
    }, [selectedTab, fields, disableInput, hasStyleTab])

    useEffect(() => {
        if (selectedTab === 'styles' && !hasStyleTab) {
            setSelectedTab(fields.length > 0 && !disableInput ? 'inputs' : 'styles')
        }
    }, [selectedTab, hasStyleTab, fields.length, disableInput])

    const commitDraftInputs = useCallback(() => {
        onInputsUpdate(finalInputs(draftInputValues))
    }, [draftInputValues, finalInputs, onInputsUpdate])

    const handleClose = useCallback(() => {
        commitDraftInputs()
        onClose()
    }, [commitDraftInputs, onClose])

    const handleCancel = useCallback(() => {
        switch (selectedTab) {
            case 'inputs': {
                setDraftInputValues(initialInputValues.current)
                onInputsUpdate(initialInputValues.current)
                break
            }
            case 'styles': {
                onOutputsUpdate(initialOutputValues.current)
                onOutputValuesPrecisionUpdate(initialOutputValuesPrecision.current)
                onDisplaySettingsUpdate({
                    showLabelOnPriceScale: initialShowLabelOnPriceScale.current,
                    showValueOnPriceScale: initialShowValueOnPriceScale.current,
                })
                break
            }
        }
        onClose()
    }, [selectedTab, onClose, onInputsUpdate, onOutputsUpdate, onOutputValuesPrecisionUpdate, onDisplaySettingsUpdate])

    const buildInputDefaultsFromFields = useCallback(() => {
        const defaultInputValues: Record<string, any> = {}
        fields.forEach((field) => {
            if (field.type === IndicatorInput_InputType.BOOLEAN) {
                defaultInputValues[field.code] = field.defval === 'true' || field.defval === true
            } else {
                defaultInputValues[field.code] = field.defval ?? ''
            }
        })
        return defaultInputValues
    }, [fields])

    const handleDefault = useCallback(() => {
        if (fields.length > 0 && !disableInput) {
            const defaultInputValues = getDefaultInputValues?.() ?? buildInputDefaultsFromFields()
            const normalized = finalInputs(defaultInputValues)
            setDraftInputValues(normalized)
            onInputsUpdate(normalized)
        }
        if (hasStyleTab && getDefaultOutputValues) {
            onOutputsUpdate(getDefaultOutputValues())
            onOutputValuesPrecisionUpdate(undefined)
            onDisplaySettingsUpdate({
                showLabelOnPriceScale: true,
                showValueOnPriceScale: true,
            })
        }
    }, [
        buildInputDefaultsFromFields,
        disableInput,
        fields.length,
        finalInputs,
        getDefaultInputValues,
        getDefaultOutputValues,
        hasStyleTab,
        onDisplaySettingsUpdate,
        onInputsUpdate,
        onOutputValuesPrecisionUpdate,
        onOutputsUpdate,
    ])

    return <CustomDialog open onClose={handleClose} maxWidth={'xs'} fullWidth
                         container={document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID)}
>
        <DraggableDialogTitle>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography variant={'h6'}>{title}</Typography>
                <ExtraSmallButton onClick={handleClose} aria-label="close" color={"inherit"}>
                    <ClearIcon/>
                </ExtraSmallButton>
            </Stack>
        </DraggableDialogTitle>
        <DialogContent sx={{ width: '100%', padding: '16px 20px' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                {tabs}
            </Box>
            <Box sx={{ display: 'grid', '& > *': { gridArea: '1 / 1' } }}>
                {fields.length > 0 && !disableInput && (
                    <Box sx={{
                        visibility: selectedTab === 'inputs' ? 'visible' : 'hidden',
                        pointerEvents: selectedTab === 'inputs' ? 'auto' : 'none',
                    }}>
                        <SeriesGroupInputs
                            fields={fields}
                            inputValues={draftInputValues}
                            onInputDraftChange={onInputDraftChange}
                            onInputChange={onInputChange}
                            indicatorOutputSources={indicatorOutputSources}
                            getSourceLabel={getSourceLabel}
                        />
                    </Box>
                )}
                {hasStyleTab && (
                    <Box sx={{
                        visibility: selectedTab === 'styles' ? 'visible' : 'hidden',
                        pointerEvents: selectedTab === 'styles' ? 'auto' : 'none',
                    }}>
                        {plotTitlesPending ? (
                            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                                <CircularProgress size={24} />
                            </Stack>
                        ) : (
                            <>
                                {hasApplicableStyleOutputs && (
                                    <SeriesGroupOutputs
                                        seriesOutputsConfig={outputConfig}
                                        seriesOrder={seriesOrder}
                                        activePlotTitles={activePlotTitles}
                                        onSeriesOutputChange={onOutputChange}
                                        showLineStyle={showLineStyle}
                                        lineStyle={lineStyle}
                                        onLineStyleChange={onLineStyleChange}
                                        disableAreaFill={disableAreaFill}
                                    />
                                )}
                                {!hideOutputValuesSettings && (
                                    <SeriesGroupOutputValuesSettings
                                        outputValuesPrecision={outputValuesPrecision}
                                        showLabelOnPriceScale={showLabelOnPriceScale}
                                        showValueOnPriceScale={showValueOnPriceScale}
                                        onOutputValuesPrecisionChange={onOutputValuesPrecisionUpdate}
                                        onShowLabelOnPriceScaleChange={(value) =>
                                            onDisplaySettingsUpdate({ showLabelOnPriceScale: value })
                                        }
                                        onShowValueOnPriceScaleChange={(value) =>
                                            onDisplaySettingsUpdate({ showValueOnPriceScale: value })
                                        }
                                    />
                                )}
                            </>
                        )}
                    </Box>
                )}
            </Box>
        </DialogContent>
        <DialogActions sx={{borderTop: 1, borderColor: 'divider', justifyContent: 'space-between'}}>
            <Button variant="outlined" sx={{
                justifyContent: 'flex-start',
                color: 'rgba(255, 255, 255, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.12)'
            }}
                    onClick={handleDefault}
            >Default</Button>
            <Box sx={{display: 'flex', gap: 1}}>
                <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
                <Button variant="contained" onClick={handleClose}>Ok</Button>
            </Box>
        </DialogActions>
    </CustomDialog>
}


export default SeriesGroupConfigurationPopup